/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as MyTV from 'store/ducks/myTv';
import { Rail } from 'ui/components';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import Constants from 'configs/Constants';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import translate from 'services/i18n/translate';
import { RailMetaData } from 'ui/pages/myTv/MainPage';
import RouterUtils from 'services/utils/RouterUtils';
import { PrivateRoutePath } from 'routes/paths';
import { Asset, EntryPoint, MenuItemType } from '@wit/vtv-x-foundation-lib';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import * as BadgesStoreUtils from 'store/utils/badgesStoreUtils';
import { StoreState } from 'store/store';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import { useRouter } from '../../../hooks/useRouter';

interface Props {
  rail: RailMetaData;
}

enum RECORDINGS_RAIL_TYPE {
  RECORDINGS,
  SCHEDULED,
}

export default function MyTVRecordings({ rail }: Props) {
  const railType = useMemo(() => {
    if (rail.item.type === MenuItemType.SCHEDULE_RECORDINGS) {
      return RECORDINGS_RAIL_TYPE.SCHEDULED;
    }
    return RECORDINGS_RAIL_TYPE.RECORDINGS;
  }, [rail.item.type]);

  const recordingsSelector = useMemo(() => {
    switch (railType) {
      case RECORDINGS_RAIL_TYPE.SCHEDULED:
        return MyTV.Selectors.getScheduledRecordings;
      case RECORDINGS_RAIL_TYPE.RECORDINGS:
      default:
        return MyTV.Selectors.getRecordings;
    }
  }, [railType]);

  const railInfo = useMemo(() => {
    if (railType === RECORDINGS_RAIL_TYPE.SCHEDULED) {
      return {
        name: translate['firstTimeUse.scheduledRecordings.label'],
        title: translate['firstTimeUse.scheduledRecordings.title'],
        description: translate['firstTimeUse.scheduledRecordings.subtitle'],
      };
    }
    return {
      name: translate['firstTimeUse.recordings.label'],
      title: translate['firstTimeUse.recordings.title'],
      description: translate['firstTimeUse.recordings.subtitle'],
    };
  }, [railType]);

  const recordingsAssets = useSelector((store: StoreState) =>
    BadgesStoreUtils.Selector.getAssetsWithBadges(
      store,
      BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.RECORDING_RAIL,
      recordingsSelector,
      railType === RECORDINGS_RAIL_TYPE.SCHEDULED ? EntryPoint.SCHEDULED_RECORDINGS : EntryPoint.RECORDINGS
    )
  );
  const history = useHistoryWithState();
  const router = useRouter();

  const onHeaderClick = () => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV_RAIL, [rail.item.id]));
  };

  const onCardClick = (asset: Asset, menuItemId?: number) => {
    router.push({
      asset,
      entryPoint: railType === RECORDINGS_RAIL_TYPE.SCHEDULED ? EntryPoint.SCHEDULED_RECORDINGS : EntryPoint.RECORDINGS,
      menuItemId,
    });
  };

  return recordingsAssets.length === 0 ? (
    <ColdStartRail
      railId={rail.item.id}
      railName={railInfo.name}
      railBackgroundImageName={RailBackgroundImageName.RECORDING}
      coldStartTitle={railInfo.title}
      railDescription={railInfo.description}
      className="no-background"
    />
  ) : (
    <Rail
      railId={rail.item.id}
      menuItemId={rail.item.id}
      className="no-background"
      railName={railInfo.name}
      channelId={rail.item.channelId}
      assets={recordingsAssets}
      hasMore={recordingsAssets.length > Constants.MAX_ASSETS_PER_RAIL}
      onHeaderClick={onHeaderClick}
      onCardClick={onCardClick}
    />
  );
}
